import { isNil, reject } from 'ramda';

export const compact = reject(isNil);
/**
 * @template U
 * @param {Array<U>} array
 * @returns {Array<U>}
 */
export const uniq = (array: any[]) => [...new Set(array)];

/**
 * Returns a new array with unique elements based on a key function.
 * Keeps the first occurrence of each unique key.
 *
 * @template T - The type of elements in the array
 * @template K - The type of the key returned by the key function
 *
 * @param {(item: T) => K} keyFn - Function to extract the key from each element
 * @param {T[]} array - The array to filter
 * @returns {T[]} A new array with unique elements based on the key
 *
 * @example
 * const users = [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }, { id: 1, name: 'Alice2' }];
 * uniqBy(user => user.id, users); // [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }]
 */
export const uniqBy = <T, K>(keyFn: (item: T) => K, array: T[]): T[] => {
  const seen = new Set<K>();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const sum = (array: any[]) => array.reduce((acc: number, value: number) => acc + value, 0);

// See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/eval#Never_use_eval!
// This is used as a replacement for lodash.get, use it if you know what you are doing !
// Otherwise, optional chaining & others are preferred.
export const get = (obj: any, path: string) => {
  const saferPath = path.replace(/;|{|\/|\\|\(|&|\|/g, '');

  const getter = Function(
    'obj',
    `{
      "use strict";
      try {
        return (obj.${saferPath});
      } catch (error) {
        return undefined;
      }
    }`
  );

  return getter(obj);
};

/**
 * Picks the entries of `dictionary` matching `ids`, preserving the dictionary's own key order.
 */
export const pickByIds = <U>(ids: string[], dictionary: Record<string, U>): Record<string, U> => {
  const dictionaryKeys = Object.keys(dictionary);
  const sortedIds = [...ids].sort((a, b) => dictionaryKeys.indexOf(a) - dictionaryKeys.indexOf(b));
  return sortedIds.reduce<Record<string, U>>(
    (acc, id) => ({
      ...acc,
      ...(dictionary[id] ? { [id]: dictionary[id] } : {})
    }),
    {}
  );
};

/**
 * Returns a new object with only the given keys (Lodash-style `pick`).
 */
export function pick<T extends object, K extends keyof T>(object: T, ...keys: K[]): Pick<T, K> {
  return keys.reduce<Pick<T, K>>((result, key) => (key in object ? { ...result, [key]: object[key] } : result), {} as Pick<T, K>);
}

export const isObject = (value: object | unknown): boolean =>
  typeof value === 'object' && !Array.isArray(value) && value !== null;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]';

/**
 * Merges `overrides` onto `base`, recursing into plain objects and replacing every other value,
 * arrays included.
 */
export const mergeDeep = <T extends object>(base: T, overrides: Partial<T>): T =>
  (Object.keys(overrides) as Array<keyof T>).reduce<T>((acc, key) => {
    const current = acc[key];
    const override = overrides[key];
    const merged = isPlainObject(current) && isPlainObject(override)
      ? mergeDeep<Record<string, unknown>>(current, override)
      : override;

    return { ...acc, [key]: merged } as T;
  }, { ...base });

/**
 * Creates a dictionary-like object with a default value for missing keys.
 * Returns a Proxy that acts as a lookup table where accessing non-existent properties
 * returns the specified default value instead of undefined.
 *
 * Commonly used for:
 * - Mapping enums/constants to values with fallbacks
 * - Configuration objects with default values
 * - Translation dictionaries with fallback text
 * - Conditional rule mappings with default behavior
 *
 * @template T - The type of values stored in the dictionary
 * @template K - The type of keys in the dictionary (extends PropertyKey)
 * @template D - The type of the default value
 *
 * @param {Record<K, T>} dictionary - The base object containing key-value mappings
 * @param {D} defaultValue - The value to return for keys not found in the dictionary
 *
 * @returns {Record<K, T> & Record<PropertyKey, D>} A proxy object that returns
 *   dictionary values for existing keys or the default value for missing keys
 *
 * @example
 * // Boolean mapping with fallback
 * const permissions = createDictionary({ admin: true, user: false }, false);
 * permissions.admin; // true
 * permissions.guest; // false (default)
 *
 * @example
 * // String mapping with fallback
 * const messages = createDictionary({
 *   success: 'Operation completed',
 *   error: 'Something went wrong'
 * }, 'Unknown status');
 * messages.success; // 'Operation completed'
 * messages.pending; // 'Unknown status' (default)
 *
 * @example
 * // Function mapping with fallback
 * const validators = createDictionary({
 *   email: (value) => /\S+@\S+\.\S+/.test(value),
 *   required: (value) => Boolean(value)
 * }, () => true);
 * validators.email('test@example.com'); // true
 * validators.unknown('any'); // true (default function)
 */
export const createDictionary = <T, O>(dictionary: Record<string | symbol, T>, defaultValue: O) =>
  new Proxy(dictionary, {
    get: (target, name) => (name in target ? target[name] : defaultValue)
  }) as unknown as Record<string | symbol, T | O>;

/**
 * Compare two objects and returns the paths of the different properties
 * @param {object} obj1
 * @param {object} obj2
 * @param {string} prefix
 * @returns {Array<string>}
 */
export const getObjectDiff = (
  obj1: Record<any, any> = {},
  obj2: Record<any, any> = {},
  prefix: string = ''
): string[] => {
  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
  return Array.from(allKeys).flatMap((key: string): any =>
    compareEntry(obj1[key], obj2[key], prefix ? `${prefix}.${key}` : key));
};

/**
 * Compare two arrays and returns the paths of the different elements
 * @param {Array} array1
 * @param {Array} array2
 * @param {string} prefix
 * @returns {Array<string>}
 */
export const compareArrays = (array1: any[], array2: any[], prefix: string = ''): string[] => {
  const differences = [];
  const maxLength = Math.max(array1.length, array2.length);

  for (let index = 0; index < maxLength; index++) {
    differences.push(...compareEntry(array1[index], array2[index], `${prefix}[${index}]`));
  }
  return differences;
};

/**
 * Compare two entries and returns the paths of the different properties
 * @param {any} entry1
 * @param {any} entry2
 * @param {string} path
 * @returns {Array<string>}
 */
const compareEntry = (entry1: any, entry2: any, path: string): string[] => {
  const differences = [];

  if (isObject(entry1) && isObject(entry2)) {
    differences.push(...getObjectDiff(entry1, entry2, path));
  } else if (Array.isArray(entry1) && Array.isArray(entry2)) {
    differences.push(...compareArrays(entry1, entry2, path));
  } else if (entry1 !== entry2) {
    differences.push(path);
  }
  return differences;
};

/**
 * Returns a new object without the given keys (Lodash-style `omit`).
 */
export function omit<T extends object, K extends keyof T>(object: T, ...keys: K[]): Omit<T, K> {
  const keysToOmit = new Set<keyof T>(keys);
  return Object.fromEntries(
    Object.entries(object).filter(([key]) => !keysToOmit.has(key as keyof T))
  ) as Omit<T, K>;
}
